import { Injectable, inject } from '@angular/core';
import {
  BsApiService,
  FileUploadService,
  GenericResponseCode,
  SimpleGlobalModalService,
  UploadUrlResponse,
} from '@brightside-web/desktop/data-access/core-services';
import { switchMap, map } from 'rxjs';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DocumentUploadService {
  private _fileUploadService = inject(FileUploadService);
  private _bsApi = inject(BsApiService);
  private _modal = inject(SimpleGlobalModalService);

  /**
   * Orchestrates:
   *  - fetch presigned URL for (entityId, fileKey, entityType)
   *  - POST the PDF bytes to S3
   *
   * Emits the upload URL response payload on success; errors on any failure.
   */
  uploadDocument(
    entityId: string,
    fileKey: string,
    entityType: string,
    pdfBytes: Uint8Array
  ): Observable<UploadUrlResponse> {
    return this._fileUploadService
      .getDocumentUploadURL(fileKey, entityId, entityType, 'pdf')
      .pipe(
        switchMap((response) => {
          if (response.result.code !== GenericResponseCode.SERVICE_OK) {
            throw new Error(`Upload URL error: ${response.result.code}`);
          }

          const payload = response.payload;
          const uploadUrl = payload.uploadURL;

          return this._fileUploadService
            .uploadDocument(uploadUrl, pdfBytes)
            .pipe(map(() => payload));
        })
      );
  }

  /**
   * Deletes a document by ID via the API.
   * Errors if the API returns a non-success response code.
   */
  deleteDocument(documentId: string) {
    return this._bsApi.del('api-mobile', '/document', { documentId }).pipe(
      tap((response) => {
        if (response.result.code !== GenericResponseCode.SERVICE_OK) {
          throw new Error(`Delete document error: ${response.result.code}`);
        }
      })
    );
  }
}
